import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiX, FiChevronLeft, FiChevronRight, FiExternalLink } from 'react-icons/fi';
import {
  buildIntroVideoPlayUrl,
  getIntroMaterialKind,
  getIntroVideoPlayUrlCandidates,
  normalizeIntroVideosList,
} from '../../../utils/assessmentStudent';
import { resolveAssessmentApiBaseUrl } from '../assessmentApiConfig';

const IntroVideoPreviewModal = ({ isOpen, onClose, testName, videos = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [urlAttempt, setUrlAttempt] = useState(0);
  const [loadError, setLoadError] = useState(false);

  const apiBase = useMemo(() => resolveAssessmentApiBaseUrl(), []);

  const mediaItems = useMemo(() => {
    return normalizeIntroVideosList(videos).map((raw) => {
      const candidates = getIntroVideoPlayUrlCandidates(raw, apiBase);
      return {
        raw,
        candidates,
        url: candidates[0] || buildIntroVideoPlayUrl(raw, apiBase),
        kind: getIntroMaterialKind(raw),
      };
    });
  }, [videos, apiBase]);

  const currentItem = mediaItems[currentIndex];
  const currentUrl = currentItem?.candidates?.[urlAttempt] || currentItem?.url || '';
  const hasMultiple = mediaItems.length > 1;

  const [resolvedUrl, setResolvedUrl] = useState(() => {
    const initialItem = normalizeIntroVideosList(videos)[0];
    if (initialItem && typeof initialItem === 'object') {
      return initialItem.objectUrl || '';
    }
    if (initialItem) {
      const candidates = getIntroVideoPlayUrlCandidates(initialItem, resolveAssessmentApiBaseUrl());
      return candidates[0] || buildIntroVideoPlayUrl(initialItem, resolveAssessmentApiBaseUrl());
    }
    return '';
  });

  useEffect(() => {
    let objectUrl = '';

    if (currentUrl && typeof currentUrl === 'object') {
      if (currentUrl.objectUrl) {
        setResolvedUrl(currentUrl.objectUrl);
      } else {
        // Fallback: see if the File object is in memory
        const fileObj = window.selectedVideoFiles?.[currentUrl.name];
        if (fileObj) {
          try {
            objectUrl = URL.createObjectURL(fileObj);
            setResolvedUrl(objectUrl);
          } catch (e) {
            setResolvedUrl('');
          }
        } else {
          setResolvedUrl('');
        }
      }
    } else {
      setResolvedUrl(currentUrl || '');
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [currentUrl]);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      setUrlAttempt(0);
      setLoadError(false);
    }
  }, [isOpen, videos]);

  useEffect(() => {
    setUrlAttempt(0);
    setLoadError(false);
  }, [currentIndex]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  const handleMediaError = useCallback((e) => {
    const mediaError = e?.target?.error;
    if (mediaError && mediaError.code === 1) {
      // MEDIA_ERR_ABORTED - ignore as it's not a real load failure
      return;
    }
    if (!currentItem) return;
    const nextAttempt = urlAttempt + 1;
    if (nextAttempt < currentItem.candidates.length) {
      setUrlAttempt(nextAttempt);
      setLoadError(false);
      return;
    }
    setLoadError(true);
  }, [currentItem, urlAttempt]);

  if (!isOpen || !mediaItems.length) {
    return null;
  }

  const renderMedia = () => {
    if (loadError || !resolvedUrl) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-12 px-4 text-center text-slate-300">
          <p className="text-sm font-semibold m-0">Unable to load this file.</p>
          {resolvedUrl && (
            <a
              href={resolvedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-bold text-red-400 hover:text-red-300"
            >
              Open in new tab <FiExternalLink />
            </a>
          )}
        </div>
      );
    }

    if (currentItem.kind === 'image') {
      return (
        <img
          key={resolvedUrl || currentIndex}
          src={resolvedUrl}
          alt="Intro material"
          className="w-full max-h-[min(70vh,480px)] object-contain rounded-lg"
          onError={handleMediaError}
        />
      );
    }

    if (currentItem.kind === 'pdf') {
      return (
        <iframe
          key={resolvedUrl || currentIndex}
          src={resolvedUrl}
          title="Intro PDF"
          className="w-full h-[min(70vh,480px)] rounded-lg bg-white border-0"
          onError={handleMediaError}
        />
      );
    }

    if (currentItem.kind === 'other') {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-12 px-4 text-center">
          <p className="text-sm text-slate-300 m-0">Preview not available for this file type.</p>
          <a
            href={resolvedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors"
          >
            Download / Open <FiExternalLink />
          </a>
        </div>
      );
    }

    return (
      <video
        key={resolvedUrl || currentIndex}
        src={resolvedUrl}
        controls
        playsInline
        preload="auto"
        className="w-full max-h-[min(70vh,480px)] rounded-lg bg-black"
        onError={handleMediaError}
      >
        Your browser does not support video playback.
      </video>
    );
  };

  return (
    <div
      className="assess-mod-intro-video-modal fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Intro video for ${testName || 'test'}`}
      onClick={onClose}
    >
      <div
        className="assess-mod-intro-video-modal-panel w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 m-0">Intro Video</p>
            <h3 className="text-base font-black text-slate-800 truncate m-0">{testName || 'Test'}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-xl border border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-50 hover:text-slate-800 transition-colors"
            aria-label="Close"
          >
            <FiX className="text-lg" />
          </button>
        </div>

        {hasMultiple && (
          <div className="flex items-center justify-between gap-2 px-4 py-2 bg-slate-50 border-b border-slate-100">
            <button
              type="button"
              disabled={currentIndex <= 0}
              onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white border border-transparent hover:border-slate-200 transition-all"
            >
              <FiChevronLeft /> Previous
            </button>
            <span className="text-xs font-bold text-slate-500">
              File {currentIndex + 1} of {mediaItems.length}
            </span>
            <button
              type="button"
              disabled={currentIndex >= mediaItems.length - 1}
              onClick={() => setCurrentIndex((i) => Math.min(mediaItems.length - 1, i + 1))}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white border border-transparent hover:border-slate-200 transition-all"
            >
              Next <FiChevronRight />
            </button>
          </div>
        )}

        <div className="p-4 bg-black min-h-[200px] flex items-center justify-center">{renderMedia()}</div>
      </div>
    </div>
  );
};

export default IntroVideoPreviewModal;
